<script lang="ts">
  /**
   * Tag input with autocomplete. Suggestions come from two sources:
   *   - public/semi tags (the server's `tags` table) via `/api/tags?q=…`
   *   - the user's private tags via IndexedDB
   *
   * For a private activity we only show the IndexedDB source (private tags
   * never round-trip through the server). For semi/public, we show server
   * suggestions; we still show the user's private matches too, labelled, so
   * they can keep a consistent vocabulary across visibility — but only the
   * private ones the user is explicitly accepting will be visible to others.
   *
   * Decision (documented in CLAUDE.md): the two sources are merged but
   * clearly labelled in the dropdown so the user knows where each match
   * comes from. A "private-only" suggestion offered for a public activity
   * becomes public the moment the user accepts it — the label warns them.
   */
  import { api } from '../lib/api';
  import { privateTagIndex } from '../lib/tagIndex';
  import type { Visibility } from '../../../shared/types';

  interface Props {
    visibility: Visibility;
    tags: string[];
    onChange: (tags: string[]) => void;
  }
  let { visibility, tags = $bindable(), onChange }: Props = $props();

  let input = $state('');
  let serverHits: { name: string; usage_count: number }[] = $state([]);
  let privateHits: { name: string; count: number }[] = $state([]);
  let timer: ReturnType<typeof setTimeout> | undefined;
  // True while the soft keyboard is composing (IME predictive text on
  // Android, hangul/kana/pinyin elsewhere). During composition keydown
  // fires with e.key === 'Unidentified' and e.keyCode === 229, so a naive
  // `e.key === 'Enter'` check misses the user's commit-and-press-enter
  // gesture. We commit on compositionend Enter via the addCurrent button
  // instead — see the keydown handler.
  let composing = $state(false);

  function add(name: string) {
    const n = name.trim().toLowerCase();
    if (!n) return;
    if (tags.includes(n)) return;
    tags = [...tags, n];
    onChange(tags);
    input = '';
    serverHits = [];
    privateHits = [];
  }

  function remove(name: string) {
    tags = tags.filter((t) => t !== name);
    onChange(tags);
  }

  function onKey(e: KeyboardEvent) {
    // Detect "commit" intent across desktop and mobile soft keyboards.
    // Android Chrome with predictive text fires keydown with key
    // 'Unidentified' + keyCode 229 during composition; Enter committed
    // afterwards still has keyCode 13 (or 'Enter'), so we accept either.
    // Comma is the same.
    if (composing) return;
    const isEnter = e.key === 'Enter' || e.keyCode === 13;
    const isComma = e.key === ',' || e.keyCode === 188;
    if (isEnter || isComma) {
      // Always preventDefault on Enter — without it the parent <form>
      // submits the whole activity when the user is just trying to add a
      // tag (especially noticeable on mobile where the soft keyboard's
      // "Done" key triggers form submit).
      e.preventDefault();
      add(input);
    } else if (e.key === 'Backspace' && !input && tags.length) {
      remove(tags[tags.length - 1]!);
    }
  }

  /** Called by the "Legg til"-button. The button is the foolproof mobile
   *  path — IME composition can swallow Enter, but a tap is a tap. */
  function addCurrent() {
    add(input);
  }

  /**
   * beforeinput fires before the browser commits a value change. We use it
   * to catch Enter on platforms where keydown doesn't deliver a usable
   * Enter event — notably Firefox on Android, which treats the soft-
   * keyboard Enter as "advance to next form field" at the input layer
   * (no keydown for us to intercept). InputEvent.inputType === 'insertLineBreak'
   * still fires here, so we get to preventDefault before focus moves.
   * Idempotent with onKey: keydown's preventDefault on Chrome cancels
   * the would-be insertion, so beforeinput never fires there.
   */
  function onBeforeInput(e: Event) {
    const ie = e as InputEvent;
    if (composing) return;
    if (ie.inputType === 'insertLineBreak') {
      e.preventDefault();
      add(input);
    }
  }

  function onType(e: Event) {
    input = (e.target as HTMLInputElement).value;
    clearTimeout(timer);
    timer = setTimeout(query, 120);
  }

  async function query() {
    const q = input.trim();
    if (!q) { serverHits = []; privateHits = []; return; }

    privateHits = await privateTagIndex.suggest(q, 10).catch(() => []);

    if (visibility !== 'private') {
      serverHits = await api.tagSuggestions(q, 10).catch(() => []);
    } else {
      serverHits = [];
    }
  }
</script>

<div class="row" role="group" aria-label="Etiketter">
  {#each tags as t (t)}
    <span class="tag">
      {t}
      <button type="button" aria-label="Fjern {t}"
              style="margin-left: 0.3rem; padding: 0 0.3rem;"
              onclick={() => remove(t)}>×</button>
    </span>
  {/each}
</div>

<div class="row" style="gap: 0.4rem; align-items: stretch;">
  <input
    type="text"
    value={input}
    oninput={onType}
    onkeydown={onKey}
    onbeforeinput={onBeforeInput}
    oncompositionstart={() => (composing = true)}
    oncompositionend={() => (composing = false)}
    placeholder="Skriv etikett, trykk Enter eller «Legg til»"
    aria-label="Ny etikett"
    enterkeyhint="done"
    autocapitalize="none"
    autocorrect="off"
    spellcheck="false"
    style="flex: 1 1 auto; min-width: 0;"
  />
  <button type="button" onclick={addCurrent} disabled={!input.trim()}
          aria-label="Legg til etikett"
          style="flex: 0 0 auto;">
    Legg til
  </button>
</div>

{#if serverHits.length || privateHits.length}
  <div class="card" style="margin-top: 0.25rem;" role="listbox" aria-label="Forslag">
    {#each serverHits as s (s.name)}
      <button type="button"
              style="display: block; width: 100%; text-align: left; border: none; background: transparent; padding: 0.25rem 0;"
              onclick={() => add(s.name)}>
        {s.name}
        <span class="muted">offentlig · {s.usage_count}</span>
      </button>
    {/each}
    {#each privateHits as p (p.name)}
      {#if !serverHits.find((s) => s.name === p.name)}
        <button type="button"
                style="display: block; width: 100%; text-align: left; border: none; background: transparent; padding: 0.25rem 0;"
                onclick={() => add(p.name)}>
          {p.name}
          <span class="muted">
            {visibility === 'private' ? 'privat' : 'kun din'} · {p.count}
          </span>
        </button>
      {/if}
    {/each}
  </div>
{/if}
